import { Link } from "react-router-dom";
import { MapPin, Users } from "lucide-react";
import type { StoreCard as StoreCardData } from "@/api/types";
import { Avatar, RatingStars, SmartImage, VerifiedBadge } from "@/components/ui";
import { useGlowPointer } from "@/motion/useGlowPointer";

/** Compact storefront card. <StoreCard store={s} /> */
export function StoreCard({ store }: { store: StoreCardData }) {
  const glow = useGlowPointer();
  return (
    <Link
      to={`/store/${store.slug}`}
      onPointerMove={glow}
      className="backlit group flex w-64 shrink-0 flex-col overflow-hidden rounded-card border border-fg/[0.06] bg-surface shadow-glow-1 transition hover:shadow-glow-2 sm:w-72"
    >
      <div className="relative h-20 bg-gradient-to-br from-primary/40 to-cyan/20">
        {store.banner && (
          <SmartImage
            src={store.banner}
            alt=""
            width={600}
            maxWidth={800}
            sizes="288px"
            compact
            className="size-full bg-transparent opacity-80"
          />
        )}
      </div>
      <div className="-mt-7 flex flex-col gap-2 px-4 pb-4">
        <Avatar
          name={store.store_name}
          src={store.logo_thumb_url || store.logo || null}
          seed={store.slug}
          size="lg"
          className="ring-4 ring-surface"
        />
        <div className="flex items-center gap-2">
          <span className="truncate font-display text-base font-semibold text-ink">{store.store_name}</span>
          {store.is_verified && <VerifiedBadge compact />}
        </div>
        {store.tagline && <p className="line-clamp-1 text-xs text-ink-muted">{store.tagline}</p>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
          <RatingStars value={Number(store.rating_avg)} count={store.rating_count} />
          <span className="inline-flex items-center gap-1">
            <Users className="size-3" aria-hidden /> {store.followers_count.toLocaleString("en-NG")}
          </span>
          {store.state_name && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="size-3" aria-hidden /> {store.state_name}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
